import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getMedias,
  linkMedia,
  unlinkMedia,
  updateMediaOrder,
  updateMediaMetadata,
} from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { isVideoMimeOrExtension } from '../../medias/public/mediaMime';
import { getFileIcon, formatFileSize } from '../../medias/public/mediaDisplay';
import MediaLightbox from '../../medias/public/MediaLightbox';
import {
  metaValueMatchesOrderId,
  collectCandidateMediaIds,
} from './orderMediaCandidates';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function mediaInOrderGallery(media, orderId) {
  return (
    media?.metadata?.some((m) => m.key === 'order_gallery' && metaValueMatchesOrderId(m, orderId)) ?? false
  );
}

function findGalleryMetaEntry(media, orderId) {
  const meta = media?.metadata?.find((m) => m.key === 'order_gallery' && metaValueMatchesOrderId(m, orderId));
  if (!meta) return null;
  const v = meta.value;
  if (typeof v === 'object' && v !== null) return v;
  return null;
}

function galleryIsPublicFromEntry(entry) {
  if (!entry || entry.public === undefined || entry.public === null) return true;
  return Boolean(entry.public);
}

function unlinkGalleryPayload(media, orderId) {
  const meta = media.metadata?.find((m) => m.key === 'order_gallery' && metaValueMatchesOrderId(m, orderId));
  if (!meta) return null;
  const metaValue = meta.value;
  if (typeof metaValue === 'object' && metaValue !== null && metaValue.id != null && String(metaValue.id).trim() !== '') {
    return String(metaValue.id);
  }
  if (typeof metaValue === 'object' && metaValue !== null) {
    return JSON.stringify(metaValue);
  }
  return String(metaValue);
}

function SortGalleryRow({ id, media, disabled, onPreview }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const isImage = media?.mime_type?.startsWith('image/');
  const isVideo = media && isVideoMimeOrExtension(media.mime_type, media.filename);
  const dragProps = disabled ? {} : { ...attributes, ...listeners };
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        px: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        mb: 0.5,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ cursor: disabled ? 'default' : 'grab', display: 'flex', color: 'text.secondary' }} {...dragProps}>
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          bgcolor: (t) => alpha(t.palette.grey[500], 0.12),
          borderRadius: 0.5,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        onClick={() => onPreview(media)}
      >
        {isImage ? (
          <Box component="img" src={`${API_URL}${media.file_path}`} alt="" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
        ) : isVideo ? (
          <Box component="video" src={`${API_URL}${media.file_path}`} muted playsInline sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          getFileIcon(media.mime_type, media.filename, 22)
        )}
      </Box>
      <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap>
        {media.filename}
      </Typography>
      <IconButton size="small" onClick={() => onPreview(media)} aria-label="preview">
        <VisibilityIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

export async function fetchOrderGalleryLightboxItems(orderId) {
  if (!orderId) return [];
  const res = await getMedias();
  const all = res.data?.medias || [];
  const inGallery = all.filter((m) => mediaInOrderGallery(m, orderId));
  const withOrder = inGallery
    .map((m) => {
      const entry = findGalleryMetaEntry(m, orderId);
      return { m, ord: entry?.order ?? 0 };
    })
    .sort((a, b) => a.ord - b.ord);
  return withOrder.map(({ m }) => ({
    id: m.id,
    file_path: m.file_path,
    filename: m.filename,
    mime_type: m.mime_type,
    file_size: m.file_size,
  }));
}

export default function OrderGalleryManagerDialog({
  open,
  onClose,
  orderId,
  order,
  formItems,
  canEdit = true,
  onAfterSave,
}) {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allMedias, setAllMedias] = useState([]);
  const [candidateIds, setCandidateIds] = useState([]);
  const [galleryOrderedIds, setGalleryOrderedIds] = useState([]);
  const [publicById, setPublicById] = useState({});
  const [lightbox, setLightbox] = useState({ open: false, media: null });
  const [lightboxMainGalleryItems, setLightboxMainGalleryItems] = useState(null);
  const [viewMainGalleryEmptyHint, setViewMainGalleryEmptyHint] = useState(false);

  const mediaById = useMemo(() => {
    const m = new Map();
    for (const x of allMedias) {
      m.set(x.id, x);
    }
    return m;
  }, [allMedias]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const openCandidateLightbox = useCallback((media) => {
    setViewMainGalleryEmptyHint(false);
    setLightboxMainGalleryItems(null);
    setLightbox({ open: true, media });
  }, []);

  const handleViewMainGallery = useCallback(async () => {
    if (!orderId) return;
    try {
      const items = await fetchOrderGalleryLightboxItems(orderId);
      if (!items.length) {
        setViewMainGalleryEmptyHint(true);
        return;
      }
      setViewMainGalleryEmptyHint(false);
      setLightboxMainGalleryItems(items);
      setLightbox({ open: true, media: items[0] || null });
    } catch {
      setViewMainGalleryEmptyHint(true);
    }
  }, [orderId]);

  const load = useCallback(async () => {
    if (!orderId || !open) return;
    setLoading(true);
    try {
      const res = await getMedias();
      const all = res.data?.medias || [];
      setAllMedias(all);
      const cand = collectCandidateMediaIds(order, formItems, orderId, all);
      const sortedCandidates = [...cand].sort((a, b) => {
        const ma = all.find((x) => x.id === a);
        const mb = all.find((x) => x.id === b);
        return String(ma?.filename || a).localeCompare(String(mb?.filename || b));
      });
      setCandidateIds(sortedCandidates);
      const galleryMedia = all.filter((m) => mediaInOrderGallery(m, orderId));
      const ordered = galleryMedia
        .map((m) => ({ m, ord: findGalleryMetaEntry(m, orderId)?.order ?? 0 }))
        .sort((x, y) => x.ord - y.ord)
        .map((x) => x.m.id);
      setGalleryOrderedIds(ordered);
      const pub = {};
      for (const m of galleryMedia) {
        const e = findGalleryMetaEntry(m, orderId);
        pub[m.id] = galleryIsPublicFromEntry(e);
      }
      setPublicById(pub);
    } catch {
      setCandidateIds([]);
      setGalleryOrderedIds([]);
      setPublicById({});
    } finally {
      setLoading(false);
    }
  }, [orderId, open, order, formItems]);

  useEffect(() => {
    if (open) {
      void load();
    }
  }, [open, load]);

  useEffect(() => {
    if (!open) {
      setLightbox({ open: false, media: null });
      setLightboxMainGalleryItems(null);
      setViewMainGalleryEmptyHint(false);
    }
  }, [open]);

  const toggleInGallery = (id) => {
    if (!canEdit) return;
    setGalleryOrderedIds((prev) => {
      if (prev.includes(id)) {
        setPublicById((p) => {
          const n = { ...p };
          delete n[id];
          return n;
        });
        return prev.filter((x) => x !== id);
      }
      setPublicById((p) => ({ ...p, [id]: p[id] !== undefined ? p[id] : true }));
      return [...prev, id];
    });
  };

  const setPublicFor = (id, v) => {
    if (!canEdit) return;
    setPublicById((p) => ({ ...p, [id]: v }));
  };

  const handleDragEnd = (event) => {
    if (!canEdit) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setGalleryOrderedIds((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleSave = async () => {
    if (!canEdit || !orderId) return;
    setSaving(true);
    try {
      const res = await getMedias();
      const all = res.data?.medias || [];
      const desired = new Set(galleryOrderedIds);
      for (const m of all) {
        if (!mediaInOrderGallery(m, orderId)) continue;
        if (desired.has(m.id)) continue;
        const payload = unlinkGalleryPayload(m, orderId);
        if (payload) {
          await unlinkMedia(m.id, 'order_gallery', payload);
        }
      }
      const resFresh = await getMedias();
      const allFresh = resFresh.data?.medias || [];
      for (let i = 0; i < galleryOrderedIds.length; i++) {
        const id = galleryOrderedIds[i];
        const m = allFresh.find((x) => x.id === id);
        if (!m) continue;
        if (!mediaInOrderGallery(m, orderId)) {
          const meta = {
            id: orderId,
            url: `/orders/${orderId}/edit`,
            title: m.filename || '',
            order: i,
            public: publicById[id] !== false,
          };
          await linkMedia(id, { order_gallery: JSON.stringify(meta) });
        }
      }
      const res2 = await getMedias();
      const all2 = res2.data?.medias || [];
      const mediaOrders = galleryOrderedIds.map((media_id, idx) => ({ media_id, order: idx }));
      if (mediaOrders.length > 0) {
        await updateMediaOrder('order_gallery', orderId, mediaOrders);
      }
      for (const id of galleryOrderedIds) {
        const m = all2.find((x) => x.id === id);
        if (!m || !mediaInOrderGallery(m, orderId)) continue;
        const title = findGalleryMetaEntry(m, orderId)?.title ?? m.filename ?? '';
        const url = findGalleryMetaEntry(m, orderId)?.url ?? `/orders/${orderId}/edit`;
        const want = publicById[id] !== false;
        await updateMediaMetadata(id, 'order_gallery', orderId, title, url, want);
      }
      if (onAfterSave) onAfterSave();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const lightboxItems = useMemo(() => {
    return candidateIds.map((cid) => mediaById.get(cid)).filter(Boolean);
  }, [candidateIds, mediaById]);

  const lightboxItemsForViewer = useMemo(() => {
    if (lightboxMainGalleryItems && lightboxMainGalleryItems.length > 0) {
      return lightboxMainGalleryItems;
    }
    if (lightboxItems.length > 0) {
      return lightboxItems;
    }
    return allMedias;
  }, [lightboxMainGalleryItems, lightboxItems, allMedias]);

  return (
    <>
      <Dialog open={open} onClose={canEdit && saving ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            pr: 2,
          }}
        >
          <Typography component="span" variant="h6" sx={{ flex: '1 1 200px', fontSize: '1.1rem' }}>
            {translate('orders.gallery_dialog_title')}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => void handleViewMainGallery()}
            disabled={!orderId || loading}
          >
            {translate('orders.gallery_view_only')}
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {viewMainGalleryEmptyHint ? (
            <Alert severity="info" sx={{ mb: 2 }} onClose={() => setViewMainGalleryEmptyHint(false)}>
              {translate('orders.gallery_main_preview_empty')}
            </Alert>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate('orders.gallery_dialog_subtitle')}
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : candidateIds.length === 0 ? (
            <Typography color="text.secondary">{translate('orders.gallery_no_medias_in_order')}</Typography>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {translate('orders.gallery_candidates_section')}
              </Typography>
              <Grid container spacing={1} sx={{ mb: 3 }}>
                {candidateIds.map((cid) => {
                  const m = mediaById.get(cid);
                  if (!m) return null;
                  const checked = galleryOrderedIds.includes(cid);
                  const isImage = m.mime_type?.startsWith('image/');
                  const isVideo = isVideoMimeOrExtension(m.mime_type, m.filename);
                  return (
                    <Grid item xs={6} sm={4} md={3} key={cid}>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: checked ? 'primary.main' : 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            aspectRatio: '1',
                            bgcolor: (t) => alpha(t.palette.grey[500], 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                          }}
                          onClick={() => openCandidateLightbox(m)}
                        >
                          {isImage ? (
                            <Box
                              component="img"
                              src={`${API_URL}${m.file_path}`}
                              alt=""
                              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
                            />
                          ) : isVideo ? (
                            <Box
                              component="video"
                              src={`${API_URL}${m.file_path}`}
                              muted
                              playsInline
                              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            getFileIcon(m.mime_type, m.filename, 40)
                          )}
                        </Box>
                        <Box sx={{ px: 0.5, py: 0.5 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={checked}
                                onChange={() => toggleInGallery(cid)}
                                disabled={!canEdit}
                              />
                            }
                            label={
                              <Typography variant="caption" noWrap sx={{ display: 'block', maxWidth: 120 }}>
                                {translate('orders.gallery_in_main')}
                              </Typography>
                            }
                          />
                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            {m.filename} · {formatFileSize(m.file_size || 0)}
                          </Typography>
                          {checked && canEdit && (
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={publicById[cid] !== false}
                                  onChange={(e) => setPublicFor(cid, e.target.checked)}
                                />
                              }
                              label={
                                <Typography variant="caption">{translate('orders.gallery_public')}</Typography>
                              }
                            />
                          )}
                          {!canEdit && checked && (
                            <Typography variant="caption" color="text.secondary">
                              {publicById[cid] !== false
                                ? translate('orders.gallery_public')
                                : translate('orders.gallery_internal')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {translate('orders.gallery_order_section')}
              </Typography>
              {galleryOrderedIds.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {translate('orders.gallery_empty_order_hint')}
                </Typography>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={galleryOrderedIds} strategy={verticalListSortingStrategy}>
                    {galleryOrderedIds.map((cid) => {
                      const m = mediaById.get(cid);
                      if (!m) return null;
                      return (
                        <SortGalleryRow
                          key={cid}
                          id={cid}
                          media={m}
                          disabled={!canEdit}
                          onPreview={(mm) => openCandidateLightbox(mm)}
                        />
                      );
                    })}
                  </SortableContext>
                </DndContext>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            {canEdit ? translate('common.cancel') : translate('common.close')}
          </Button>
          {canEdit && (
            <Button onClick={handleSave} variant="contained" disabled={saving || loading}>
              {saving ? translate('common.loading') : translate('orders.gallery_save')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <MediaLightbox
        open={lightbox.open}
        onClose={() => {
          setLightbox({ open: false, media: null });
          setLightboxMainGalleryItems(null);
        }}
        items={lightboxItemsForViewer}
        activeMedia={lightbox.media}
        onNavigate={(mm) => setLightbox({ open: true, media: mm })}
        apiBaseUrl={API_URL}
        desktopOpenContext={orderId ? { domain: 'order', entityId: orderId } : null}
      />
    </>
  );
}
